import React, {useCallback, useEffect, useMemo, useRef, useState} from "react"
import {CiBranch, CiBuildArtifact, CiTarget, useDevicesStore} from "../../store/devicesStore"

type RtlSource = "computer" | "ci"

interface FirmwareUploadSectionProps {
	firmwareUploadLoading: boolean
	firmwareUploadSuccess: string | null
	firmwareUploadError: string | null
	selectedFile: File | null
	selectedDevices: string[]
	/** Developer: uploads go only to selected devices; the "all devices" path is not offered. */
	requireSelection?: boolean
	onFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void
	onFileUpload: () => void
	onClearFirmwareStatus: () => void
	rtlUploadLoading: boolean
	rtlUploadSuccess: string | null
	rtlUploadError: string | null
	selectedRtlFile: File | null
	onRtlFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void
	onRtlFileUpload: () => void
	/** RTL upload of a CI build (S3 key) to the single selected device. */
	onRtlCiUpload?: (key: string) => void
	/** Type of the selected device when exactly one device is selected, else null. */
	singleSelectedDeviceType?: "angel" | "falcon" | "unknown" | null
	/** Offer "From S3 (latest CI build)" in the RTL file chooser (admin / developer). */
	allowCiSource?: boolean
	onClearRtlStatus: () => void
}

const TYPE_LABEL: Record<CiTarget, string> = {angel: "Angel", falcon: "Falcon"}

const formatBuildTime = (iso: string): string => {
	const t = Date.parse(iso)
	return Number.isNaN(t) ? iso : new Date(t).toLocaleString()
}

const FirmwareUploadSection: React.FC<FirmwareUploadSectionProps> = ({
	firmwareUploadLoading,
	firmwareUploadSuccess,
	firmwareUploadError,
	selectedFile,
	selectedDevices,
	requireSelection = false,
	onFileSelect,
	onFileUpload,
	onClearFirmwareStatus,
	rtlUploadLoading,
	rtlUploadSuccess,
	rtlUploadError,
	selectedRtlFile,
	onRtlFileSelect,
	onRtlFileUpload,
	onRtlCiUpload,
	singleSelectedDeviceType = null,
	allowCiSource = false,
	onClearRtlStatus,
}) => {
	const missingSelection = requireSelection && selectedDevices.length === 0
	const listCiLatestBuilds = useDevicesStore((s) => s.listCiLatestBuilds)
	const ciBuildFinishedAt = useDevicesStore((s) => s.ciBuildFinishedAt)

	const [rtlSource, setRtlSource] = useState<RtlSource>("computer")
	const [chooserOpen, setChooserOpen] = useState(false)
	const [ciBuilds, setCiBuilds] = useState<CiBuildArtifact[]>([])
	const [ciLoading, setCiLoading] = useState(false)
	const [ciError, setCiError] = useState<string | null>(null)
	const [ciBranch, setCiBranch] = useState<CiBranch | null>(null)
	const [ciVariant, setCiVariant] = useState<"crc" | "no_crc">("crc")
	const rtlInputRef = useRef<HTMLInputElement>(null)
	const chooserRef = useRef<HTMLDivElement>(null)
	const ciRequestSeq = useRef(0)

	// S3 (CI build) is only possible for exactly one selected device of a known type.
	const ciDeviceType: CiTarget | null =
		singleSelectedDeviceType === "angel" || singleSelectedDeviceType === "falcon"
			? singleSelectedDeviceType
			: null
	const ciAvailable = allowCiSource && !!onRtlCiUpload && selectedDevices.length === 1 && !!ciDeviceType
	const ciUnavailableReason =
		selectedDevices.length !== 1
			? "Upload from S3 is possible only with exactly one device selected. With no devices or several devices selected, upload from computer only."
			: !ciDeviceType
				? "Device type (Angel / Falcon) of the selected device is not known yet - wait for its shadow to load."
				: null

	const loadCiBuilds = useCallback(
		async (deviceType: CiTarget) => {
			const seq = ++ciRequestSeq.current
			setCiLoading(true)
			setCiError(null)
			try {
				const builds = await listCiLatestBuilds(deviceType)
				if (seq !== ciRequestSeq.current) return
				setCiBuilds(builds)
				// Preselect the newest build: its branch and variant.
				setCiBranch(builds[0]?.branch ?? null)
				setCiVariant(builds[0]?.variant ?? "crc")
			} catch (error: any) {
				if (seq !== ciRequestSeq.current) return
				setCiBuilds([])
				setCiBranch(null)
				setCiError(error?.message || "Failed to list CI builds")
			} finally {
				if (seq === ciRequestSeq.current) setCiLoading(false)
			}
		},
		[listCiLatestBuilds],
	)

	// Selection no longer exactly one device (or type unknown): fall back to upload from computer.
	// The single device switched to the other type: reload the builds of the new folder.
	const loadedForType = useRef<CiTarget | null>(null)
	useEffect(() => {
		if (!ciAvailable || !ciDeviceType) {
			if (rtlSource === "ci") {
				ciRequestSeq.current++
				setRtlSource("computer")
				setCiBuilds([])
				setCiError(null)
				setCiLoading(false)
			}
			loadedForType.current = null
			return
		}
		if (rtlSource === "ci" && loadedForType.current !== ciDeviceType) {
			loadedForType.current = ciDeviceType
			loadCiBuilds(ciDeviceType)
		}
	}, [ciAvailable, ciDeviceType, rtlSource, loadCiBuilds])

	// A console CI build just finished: refresh the list if the S3 picker is open.
	useEffect(() => {
		if (ciBuildFinishedAt && rtlSource === "ci" && ciDeviceType) loadCiBuilds(ciDeviceType)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ciBuildFinishedAt])

	// Close the chooser menu on outside click / Escape.
	useEffect(() => {
		if (!chooserOpen) return
		const onDown = (e: MouseEvent) => {
			if (chooserRef.current && !chooserRef.current.contains(e.target as Node)) setChooserOpen(false)
		}
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") setChooserOpen(false)
		}
		document.addEventListener("mousedown", onDown)
		document.addEventListener("keydown", onKey)
		return () => {
			document.removeEventListener("mousedown", onDown)
			document.removeEventListener("keydown", onKey)
		}
	}, [chooserOpen])

	const chooseComputer = () => {
		setChooserOpen(false)
		if (rtlSource === "ci") {
			ciRequestSeq.current++
			setRtlSource("computer")
			loadedForType.current = null
		}
		rtlInputRef.current?.click()
	}

	const chooseCi = () => {
		if (!ciAvailable || !ciDeviceType) return
		setChooserOpen(false)
		onClearRtlStatus()
		if (rtlSource === "ci") {
			// Already picking from S3: just reload the list.
			loadedForType.current = ciDeviceType
			loadCiBuilds(ciDeviceType)
		} else {
			loadedForType.current = null
			setRtlSource("ci")
		}
	}

	const ciBranches = useMemo(
		() => Array.from(new Set(ciBuilds.map((b) => b.branch))) as CiBranch[],
		[ciBuilds],
	)
	// Newest build of the chosen branch + variant (ciBuilds is newest first).
	const ciSelectedBuild = useMemo(
		() => ciBuilds.find((b) => b.branch === ciBranch && b.variant === ciVariant) ?? null,
		[ciBuilds, ciBranch, ciVariant],
	)
	const inCiMode = rtlSource === "ci" && ciAvailable

	const handleRtlUploadClick = () => {
		if (inCiMode) {
			if (ciSelectedBuild && onRtlCiUpload) onRtlCiUpload(ciSelectedBuild.key)
			return
		}
		onRtlFileUpload()
	}
	const rtlUploadDisabled = inCiMode
		? !ciSelectedBuild || ciLoading || rtlUploadLoading
		: !selectedRtlFile || rtlUploadLoading || missingSelection

	const selectionHint = missingSelection && (
		<div className="mt-2 text-sm text-sky-700 dark:text-sky-300 bg-sky-50 dark:bg-sky-900/30 p-3 rounded border border-sky-200 dark:border-sky-700">
			Select one or more of your devices in the table to upload. Uploading to all devices is
			available to administrators only.
		</div>
	)

	return (
		<div className="space-y-6">
			<div className="border-t border-gray-200 dark:border-gray-700 pt-4">
				<h3 className="font-semibold mb-3 text-gray-900 dark:text-gray-100">
					Firmware Radar Upload (.bin files)
				</h3>
				<div className="flex gap-4 items-center flex-wrap">
					<div className="flex items-center gap-2">
						<label
							className={`cursor-pointer px-4 py-2 rounded text-sm font-semibold bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/60 border border-blue-200 dark:border-blue-700 transition-colors ${
								firmwareUploadLoading ? "opacity-50 cursor-not-allowed" : ""
							}`}>
							Wybierz plik
							<input
								id="binFileInput"
								type="file"
								accept=".bin"
								onChange={onFileSelect}
								disabled={firmwareUploadLoading}
								className="hidden"
							/>
						</label>
						<span className="text-sm text-gray-600 dark:text-gray-300">
							{selectedFile
								? `${selectedFile.name} (${(selectedFile.size / 1024).toFixed(2)} KB)`
								: "Nie wybrano pliku"}
						</span>
					</div>
					<button
						onClick={onFileUpload}
						disabled={!selectedFile || firmwareUploadLoading || missingSelection}
						title={missingSelection ? "Select devices in the table first" : undefined}
						className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed">
						{firmwareUploadLoading ? "Uploading..." : "Upload Firmware"}
					</button>
				</div>
				{selectionHint}
				{selectedDevices.length > 0 && (
					<div className="mt-2 text-sm text-blue-600 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 p-3 rounded border border-blue-200 dark:border-blue-700">
						<span className="font-semibold">
							{selectedDevices.length} device{selectedDevices.length > 1 ? "s" : ""} selected for
							upload
						</span>
					</div>
				)}
				{firmwareUploadSuccess && (
					<div className="mt-2 text-sm text-green-600 dark:text-green-300 bg-green-50 dark:bg-green-900/30 p-3 rounded border border-green-200 dark:border-green-700 flex justify-between items-center">
						<span>{firmwareUploadSuccess}</span>
						<button
							onClick={onClearFirmwareStatus}
							className="text-green-700 dark:text-green-300 hover:text-green-900 dark:hover:text-green-100 font-bold">
							×
						</button>
					</div>
				)}
				{firmwareUploadError && (
					<div className="mt-2 text-sm text-red-600 dark:text-red-300 bg-red-50 dark:bg-red-900/30 p-3 rounded border border-red-200 dark:border-red-700 flex justify-between items-center">
						<span>{firmwareUploadError}</span>
						<button
							onClick={onClearFirmwareStatus}
							className="text-red-700 dark:text-red-300 hover:text-red-900 dark:hover:text-red-100 font-bold">
							×
						</button>
					</div>
				)}
			</div>

			<div className="border-t border-gray-200 dark:border-gray-700 pt-4">
				<h3 className="font-semibold mb-3 text-gray-900 dark:text-gray-100">
					RTL Software Upload (.bin files)
				</h3>
				<div className="flex gap-4 items-center flex-wrap">
					<div className="flex items-center gap-2">
						<input
							id="rtlFileInput"
							ref={rtlInputRef}
							type="file"
							accept=".bin"
							onChange={onRtlFileSelect}
							disabled={rtlUploadLoading}
							className="hidden"
						/>
						<div className="relative" ref={chooserRef}>
							<button
								type="button"
								onClick={() => (allowCiSource ? setChooserOpen((v) => !v) : rtlInputRef.current?.click())}
								disabled={rtlUploadLoading}
								aria-haspopup={allowCiSource ? "menu" : undefined}
								aria-expanded={allowCiSource ? chooserOpen : undefined}
								className="px-4 py-2 rounded text-sm font-semibold bg-green-50 dark:bg-green-900/40 text-green-700 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/60 border border-green-200 dark:border-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
								Choose file{allowCiSource ? " \u25BE" : ""}
							</button>
							{chooserOpen && (
								<div
									role="menu"
									className="absolute z-20 mt-1 w-72 rounded border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-lg py-1">
									<button
										type="button"
										role="menuitem"
										onClick={chooseComputer}
										className="w-full text-left px-3 py-2 text-sm text-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700">
										From computer
									</button>
									<button
										type="button"
										role="menuitem"
										onClick={chooseCi}
										disabled={!ciAvailable}
										title={ciUnavailableReason || undefined}
										className="w-full text-left px-3 py-2 text-sm text-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:text-gray-400 dark:disabled:text-gray-500 disabled:hover:bg-transparent disabled:cursor-not-allowed">
										From S3 (latest CI build)
										{ciAvailable && ciDeviceType && (
											<span className="block text-xs text-gray-500 dark:text-gray-400">
												{TYPE_LABEL[ciDeviceType]} build for {selectedDevices[0]}
											</span>
										)}
										{!ciAvailable && ciUnavailableReason && (
											<span className="block text-xs text-gray-400 dark:text-gray-500">
												{ciUnavailableReason}
											</span>
										)}
									</button>
								</div>
							)}
						</div>
						<span className="text-sm text-gray-600 dark:text-gray-300">
							{inCiMode
								? ciSelectedBuild
									? `S3: ${ciSelectedBuild.key.split("/").pop()}`
									: ciLoading
										? "S3: loading builds..."
										: "S3: no build selected"
								: selectedRtlFile
									? `${selectedRtlFile.name} (${(selectedRtlFile.size / 1024).toFixed(2)} KB)`
									: "Nie wybrano pliku"}
						</span>
					</div>
					<button
						onClick={handleRtlUploadClick}
						disabled={rtlUploadDisabled}
						title={!inCiMode && missingSelection ? "Select devices in the table first" : undefined}
						className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed">
						{rtlUploadLoading ? "Uploading..." : "Upload RTL Software"}
					</button>
				</div>
				{inCiMode && ciDeviceType && (
					<div className="mt-2 text-sm p-3 rounded border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/40 text-gray-700 dark:text-gray-200">
						<div className="flex flex-wrap items-center gap-x-4 gap-y-2">
							<span>
								<span className="text-gray-500 dark:text-gray-400">CI build ({TYPE_LABEL[ciDeviceType]}) for</span>{" "}
								<span className="font-mono">{selectedDevices[0]}</span>
							</span>
							{ciBranches.length > 1 && (
								<div className="inline-flex rounded border border-gray-300 dark:border-gray-600 overflow-hidden">
									{ciBranches.map((b) => (
										<button
											key={b}
											type="button"
											onClick={() => setCiBranch(b)}
											disabled={rtlUploadLoading}
											className={`px-3 py-1 text-xs ${
												ciBranch === b
													? "bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-900"
													: "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
											}`}>
											{b}
										</button>
									))}
								</div>
							)}
							{ciBuilds.length > 0 && (
								<div className="inline-flex rounded border border-gray-300 dark:border-gray-600 overflow-hidden">
									{(["crc", "no_crc"] as const).map((v) => (
										<button
											key={v}
											type="button"
											onClick={() => setCiVariant(v)}
											disabled={rtlUploadLoading}
											className={`px-3 py-1 text-xs ${
												ciVariant === v
													? "bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-900"
													: "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
											}`}>
											{v === "crc" ? "with CRC" : "without CRC"}
										</button>
									))}
								</div>
							)}
							<button
								type="button"
								onClick={() => loadCiBuilds(ciDeviceType)}
								disabled={ciLoading}
								className="text-xs text-blue-600 dark:text-blue-300 hover:underline disabled:opacity-50">
								{ciLoading ? "Loading..." : "Reload"}
							</button>
						</div>
						{ciError && <div className="mt-2 text-red-600 dark:text-red-300">{ciError}</div>}
						{!ciLoading && !ciError && ciBuilds.length === 0 && (
							<div className="mt-2 text-gray-500 dark:text-gray-400">
								No CI builds found for {TYPE_LABEL[ciDeviceType]}. Start one with "Make {TYPE_LABEL[ciDeviceType]}".
							</div>
						)}
						{ciBuilds.length > 0 && !ciSelectedBuild && (
							<div className="mt-2 text-gray-500 dark:text-gray-400">
								No {ciVariant === "crc" ? "with CRC" : "without CRC"} build on branch {ciBranch}.
							</div>
						)}
						{ciSelectedBuild && (
							<div className="mt-2 grid gap-x-4 gap-y-1 sm:grid-cols-[auto_1fr] text-xs">
								<span className="text-gray-500 dark:text-gray-400">Branch</span>
								<span className="font-semibold">{ciSelectedBuild.branch}</span>
								<span className="text-gray-500 dark:text-gray-400">Built</span>
								<span>{formatBuildTime(ciSelectedBuild.builtAt)}</span>
								<span className="text-gray-500 dark:text-gray-400">Variant</span>
								<span>{ciSelectedBuild.variant === "crc" ? "with CRC" : "without CRC (4 B shorter)"}</span>
								<span className="text-gray-500 dark:text-gray-400">Size</span>
								<span>{(ciSelectedBuild.size / 1024).toFixed(2)} KB</span>
								<span className="text-gray-500 dark:text-gray-400">S3 key</span>
								<span className="font-mono break-all">{ciSelectedBuild.key}</span>
							</div>
						)}
					</div>
				)}
				{selectionHint}
				{selectedDevices.length > 0 && (
					<div className="mt-2 text-sm text-blue-600 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 p-3 rounded border border-blue-200 dark:border-blue-700">
						<span className="font-semibold">
							{selectedDevices.length} device{selectedDevices.length > 1 ? "s" : ""} selected for
							upload
						</span>
					</div>
				)}
				{rtlUploadSuccess && (
					<div className="mt-2 text-sm text-green-600 dark:text-green-300 bg-green-50 dark:bg-green-900/30 p-3 rounded border border-green-200 dark:border-green-700 flex justify-between items-center">
						<span>{rtlUploadSuccess}</span>
						<button
							onClick={onClearRtlStatus}
							className="text-green-700 dark:text-green-300 hover:text-green-900 dark:hover:text-green-100 font-bold">
							×
						</button>
					</div>
				)}
				{rtlUploadError && (
					<div className="mt-2 text-sm text-red-600 dark:text-red-300 bg-red-50 dark:bg-red-900/30 p-3 rounded border border-red-200 dark:border-red-700 flex justify-between items-center">
						<span>{rtlUploadError}</span>
						<button
							onClick={onClearRtlStatus}
							className="text-red-700 dark:text-red-300 hover:text-red-900 dark:hover:text-red-100 font-bold">
							×
						</button>
					</div>
				)}
			</div>
		</div>
	)
}

export default FirmwareUploadSection
